import { SaveLeadFormDraftParams } from '@plura/shared/types';
import { v4 as uuidv4 } from 'uuid';
import themes from './_themes';
import { generateCustomID } from '@plura/shared/utils';

// Export a function that generates fresh UUIDs each time it's called
const generateProductResearchSurveyTemplate = (): SaveLeadFormDraftParams => {
  const formId = uuidv4();
  const collectionId = generateCustomID(18)
  const workspaceId = uuidv4();

  // Generate new UUIDs for all blocks to ensure uniqueness
  const welcomeScreenId = uuidv4();
  const welcomeScreenBlockId = uuidv4();
  const contactInfoId = uuidv4();
  const contactInfoBlockId = uuidv4();
  const multipleChoice1Id = uuidv4();
  const multipleChoice1BlockId = uuidv4();
  const multipleChoice2Id = uuidv4();
  const multipleChoice2BlockId = uuidv4();
  const multipleChoice3Id = uuidv4();
  const multipleChoice3BlockId = uuidv4();
  const longText1Id = uuidv4();
  const longText1BlockId = uuidv4();
  const multipleChoice4Id = uuidv4();
  const multipleChoice4BlockId = uuidv4();
  const optionScaleId = uuidv4();
  const optionScaleBlockId = uuidv4();
  const longText2Id = uuidv4();
  const longText2BlockId = uuidv4();
  const endScreenId = uuidv4();
  const endScreenBlockId = uuidv4();

  // Generate new UUIDs for select options
  const mc1Option1Id = uuidv4();
  const mc1Option2Id = uuidv4();
  const mc1Option3Id = uuidv4();
  const mc1Option4Id = uuidv4();
  const mc2Option1Id = uuidv4();
  const mc2Option2Id = uuidv4();
  const mc2Option3Id = uuidv4();
  const mc2Option4Id = uuidv4();
  const mc4Option1Id = uuidv4();
  const mc4Option2Id = uuidv4();
  const mc4Option3Id = uuidv4();
  const mc4Option4Id = uuidv4();

  // Generate new UUIDs for media
  const welcomeMediaId = uuidv4();
  const contactInfoMediaId = uuidv4();
  const multipleChoice1MediaId = uuidv4();
  const multipleChoice2MediaId = uuidv4();
  const multipleChoice3MediaId = uuidv4();
  const longText1MediaId = uuidv4();
  const multipleChoice4MediaId = uuidv4();
  const optionScaleMediaId = uuidv4();
  const longText2MediaId = uuidv4();
  const endScreenMediaId = uuidv4();

  // Generate new UUIDs for logic items and conditions
  const logic1Id = uuidv4();
  const logic2Id = uuidv4();
  const logic3Id = uuidv4();
  const condition1Id = uuidv4();
  const condition2Id = uuidv4();

  return {
    ...themes.blackMidnight,
    "collectionId": collectionId,
    "name": "Product Research Survey",
    "overview": "This is a form to collect leads",
    "about": "We're looking to connect with new leads",
    "tone": "Casual + Professional",
    "hideBuiltWithBadge": false,
    "closed": false,
    "closedMessage": "",
    "legalDisclaimer": false,
    "legalMessage": "",
    "notifications": false,
    "notificationEmail": "",
    "logoUrl": "https://s3.amazonaws.com/leadrr-prod-uploads/uploads/0d5b697e-e386-46ef-a2c7-34044737a773/GOLDLEAF-CO.png",
    "transitionType": "SLIDE_VERTICAL",
    "transitionSpeed": 500,
    "skipWelcomeScreen": false,
    "googleTagManager": "",
    "metaPixel": "",
    "assistantId": `asst_${collectionId}`,
    "fontSizeSetting": "MEDIUM",
    "customEndScreen": false,
    "hiddenFields": [],
    "journeyItems": [
      {
        "type": "WELCOME_SCREEN",
        "strictness": "COMMON",
        "contentAlignment": "LEFT",
        "mediaPosition": "SPLIT_RIGHT",
        "mobileMediaPosition": "MOBILE_POSTER_TOP",
        "id": welcomeScreenId,
        "blockId": welcomeScreenBlockId,
        "content": "Help us shape the next generation of workflow tools",
        "manditory": false,
        "order": 0,
        "selectOptions": [],
        "multiSelect": null,
        "youtubeVideoURL": null,
        "internalName": null,
        "consentTitle": null,
        "minRange": null,
        "maxRange": null,
        "minLabel": null,
        "maxLabel": null,
        "secondaryContent": "Your insights help us build smarter ways for teams to automate everyday tasks.",
        "thirdContent": "Start now",
        "forthContent": null,
        "icon": null,
        "additionalSettings": null,
        "targetBlockId": null,
        "endOnCompletion": false,
        "scoreValues": null,
        "responseTableVisible": null,
        "horizontalAlignment": true,
        "opacity": 100,
        "media": {
          "provider": "UNSPLASH",
          "id": welcomeMediaId,
          "type": "image",
          "name": "Two abstract organic shapes with vibrant gradient colors",
          "link": "https://unsplash.com/photos/0JokpSkuBeU/download?ixid=M3w1Nzk2MDF8MHwxfHNlYXJjaHw0fHxncmFkaWVudCUyMGJsb2J8ZW58MHx8fHwxNzY0NTU3OTk2fDA",
          "workspaceId": workspaceId,
          "createdAt": new Date(),
          "updatedAt": new Date(),
          "author": "Viktor Forgacs",
          "authorProfile": "https://unsplash.com/@sonance",
          "downloadLink": "https://api.unsplash.com/photos/0JokpSkuBeU/download?ixid=M3w1Nzk2MDF8MHwxfHNlYXJjaHw0fHxncmFkaWVudCUyMGJsb2J8ZW58MHx8fHwxNzY0NTU3OTk2fDA"
        },
        "createdAt": new Date(),
        "updatedAt": new Date(),
        "formId": formId,
        "logicItems": []
      },
      {
        "type": "CONTACT_INFO",
        "strictness": "COMMON",
        "contentAlignment": "LEFT",
        "mediaPosition": "FULL",
        "mobileMediaPosition": "MOBILE_FULL",
        "id": contactInfoId,
        "blockId": contactInfoBlockId,
        "content": "What's your name and email?",
        "manditory": true,
        "order": 1,
        "selectOptions": [],
        "multiSelect": null,
        "youtubeVideoURL": null,
        "internalName": null,
        "consentTitle": null,
        "minRange": null,
        "maxRange": null,
        "minLabel": null,
        "maxLabel": null,
        "secondaryContent": "",
        "thirdContent": null,
        "forthContent": null,
        "icon": null,
        "additionalSettings": {
          "first_name": {
            "required": false,
            "visible": true
          },
          "last_name": {
            "required": false,
            "visible": false
          },
          "email": {
            "required": false,
            "visible": true
          },
          "phone": {
            "required": false,
            "visible": false
          },
          "company": {
            "required": false,
            "visible": false
          }
        },
        "targetBlockId": null,
        "endOnCompletion": false,
        "scoreValues": null,
        "responseTableVisible": null,
        "horizontalAlignment": true,
        "opacity": 100,
        "media": {
          "provider": "UNSPLASH",
          "id": contactInfoMediaId,
          "type": "image",
          "name": "purple and pink light illustration",
          "link": "https://unsplash.com/photos/n2XqPm7Bqhk/download?ixid=M3w1Nzk2MDF8MHwxfHNlYXJjaHwxfHxncmFkaWVudCUyMGJsYWNrfGVufDB8fHx8MTc2NDU1ODE0M3ww",
          "workspaceId": workspaceId,
          "createdAt": new Date(),
          "updatedAt": new Date(),
          "author": "Codioful (Formerly Gradienta)",
          "authorProfile": "https://unsplash.com/@codioful",
          "downloadLink": "https://api.unsplash.com/photos/n2XqPm7Bqhk/download?ixid=M3w1Nzk2MDF8MHwxfHNlYXJjaHwxfHxncmFkaWVudCUyMGJsYWNrfGVufDB8fHx8MTc2NDU1ODE0M3ww"
        },
        "createdAt": new Date(),
        "updatedAt": new Date(),
        "formId": formId,
        "logicItems": []
      },
      {
        "type": "MULTIPLE_CHOICE",
        "strictness": "COMMON",
        "contentAlignment": "LEFT",
        "mediaPosition": "FULL",
        "mobileMediaPosition": "MOBILE_FULL",
        "id": multipleChoice1Id,
        "blockId": multipleChoice1BlockId,
        "content": `Thanks, {{${contactInfoBlockId}__first_name}}! What kind of work do you do?`,
        "manditory": true,
        "order": 2,
        "selectOptions": [
          {
            "id": mc1Option1Id,
            "order": 0,
            "value": "Operations"
          },
          {
            "id": mc1Option2Id,
            "order": 1,
            "value": "Customer support"
          },
          {
            "id": mc1Option3Id,
            "order": 2,
            "value": "Team leadership"
          },
          {
            "id": mc1Option4Id,
            "order": 3,
            "value": "Freelancer / solo operator"
          }
        ],
        "multiSelect": null,
        "youtubeVideoURL": null,
        "internalName": null,
        "consentTitle": null,
        "minRange": null,
        "maxRange": null,
        "minLabel": null,
        "maxLabel": null,
        "secondaryContent": "Choose the closest match.",
        "thirdContent": null,
        "forthContent": null,
        "icon": null,
        "additionalSettings": null,
        "targetBlockId": null,
        "endOnCompletion": false,
        "scoreValues": null,
        "responseTableVisible": null,
        "horizontalAlignment": true,
        "opacity": 100,
        "media": {
          "provider": "UNSPLASH",
          "id": multipleChoice1MediaId,
          "type": "image",
          "name": "purple and pink light illustration",
          "link": "https://unsplash.com/photos/n2XqPm7Bqhk/download?ixid=M3w1Nzk2MDF8MHwxfHNlYXJjaHwxfHxncmFkaWVudCUyMGJsYWNrfGVufDB8fHx8MTc2NDU1ODE0M3ww",
          "workspaceId": workspaceId,
          "createdAt": new Date(),
          "updatedAt": new Date(),
          "author": "Codioful (Formerly Gradienta)",
          "authorProfile": "https://unsplash.com/@codioful",
          "downloadLink": "https://api.unsplash.com/photos/n2XqPm7Bqhk/download?ixid=M3w1Nzk2MDF8MHwxfHNlYXJjaHwxfHxncmFkaWVudCUyMGJsYWNrfGVufDB8fHx8MTc2NDU1ODE0M3ww"
        },
        "createdAt": new Date(),
        "updatedAt": new Date(),
        "formId": formId,
        "logicItems": []
      },
      {
        "type": "MULTIPLE_CHOICE",
        "strictness": "COMMON",
        "contentAlignment": "LEFT",
        "mediaPosition": "SPLIT_RIGHT",
        "mobileMediaPosition": "MOBILE_POSTER_TOP",
        "id": multipleChoice2Id,
        "blockId": multipleChoice2BlockId,
        "content": "Roughly how big is your team?",
        "manditory": true,
        "order": 3,
        "selectOptions": [
          {
            "id": mc2Option1Id,
            "order": 0,
            "value": "Just me"
          },
          {
            "id": mc2Option2Id,
            "order": 1,
            "value": "2-5"
          },
          {
            "id": mc2Option3Id,
            "order": 2,
            "value": "6-20"
          },
          {
            "id": mc2Option4Id,
            "order": 3,
            "value": "Over 20"
          }
        ],
        "multiSelect": null,
        "youtubeVideoURL": null,
        "internalName": null,
        "consentTitle": null,
        "minRange": null,
        "maxRange": null,
        "minLabel": null,
        "maxLabel": null,
        "secondaryContent": null,
        "thirdContent": null,
        "forthContent": null,
        "icon": null,
        "additionalSettings": null,
        "targetBlockId": null,
        "endOnCompletion": false,
        "scoreValues": null,
        "responseTableVisible": null,
        "horizontalAlignment": true,
        "opacity": 100,
        "media": {
          "provider": "UNSPLASH",
          "id": multipleChoice2MediaId,
          "type": "image",
          "name": "a computer generated image of a human hand",
          "link": "https://unsplash.com/photos/icmWtTaqb2k/download?ixid=M3w1Nzk2MDF8MHwxfHNlYXJjaHw4M3x8Z3JhZGllbnQlMjBibG9ifGVufDB8fHx8MTc2NDU1ODMxN3ww",
          "workspaceId": workspaceId,
          "createdAt": new Date(),
          "updatedAt": new Date(),
          "author": "Milad Fakurian",
          "authorProfile": "https://unsplash.com/@fakurian",
          "downloadLink": "https://api.unsplash.com/photos/icmWtTaqb2k/download?ixid=M3w1Nzk2MDF8MHwxfHNlYXJjaHw4M3x8Z3JhZGllbnQlMjBibG9ifGVufDB8fHx8MTc2NDU1ODMxN3ww"
        },
        "createdAt": new Date(),
        "updatedAt": new Date(),
        "formId": formId,
        "logicItems": []
      },
      {
        "type": "MULTIPLE_CHOICE",
        "strictness": "COMMON",
        "contentAlignment": "LEFT",
        "mediaPosition": "FULL",
        "mobileMediaPosition": "MOBILE_FULL",
        "id": multipleChoice3Id,
        "blockId": multipleChoice3BlockId,
        "content": "Does your team currently automate any of your internal workflows?",
        "manditory": true,
        "order": 4,
        "selectOptions": [
          {
            "id": "yes",
            "order": 0,
            "value": "Yes"
          },
          {
            "id": "no",
            "order": 1,
            "value": "No"
          }
        ],
        "multiSelect": null,
        "youtubeVideoURL": null,
        "internalName": null,
        "consentTitle": null,
        "minRange": null,
        "maxRange": null,
        "minLabel": null,
        "maxLabel": null,
        "secondaryContent": null,
        "thirdContent": null,
        "forthContent": null,
        "icon": null,
        "additionalSettings": null,
        "targetBlockId": null,
        "endOnCompletion": false,
        "scoreValues": null,
        "responseTableVisible": null,
        "horizontalAlignment": true,
        "opacity": 100,
        "media": {
          "provider": "UNSPLASH",
          "id": multipleChoice3MediaId,
          "type": "image",
          "name": "purple and pink light illustration",
          "link": "https://unsplash.com/photos/n2XqPm7Bqhk/download?ixid=M3w1Nzk2MDF8MHwxfHNlYXJjaHwxfHxncmFkaWVudCUyMGJsYWNrfGVufDB8fHx8MTc2NDU1ODE0M3ww",
          "workspaceId": workspaceId,
          "createdAt": new Date(),
          "updatedAt": new Date(),
          "author": "Codioful (Formerly Gradienta)",
          "authorProfile": "https://unsplash.com/@codioful",
          "downloadLink": "https://api.unsplash.com/photos/n2XqPm7Bqhk/download?ixid=M3w1Nzk2MDF8MHwxfHNlYXJjaHwxfHxncmFkaWVudCUyMGJsYWNrfGVufDB8fHx8MTc2NDU1ODE0M3ww"
        },
        "createdAt": new Date(),
        "updatedAt": new Date(),
        "formId": formId,
        "logicItems": [
          {
            "conditionType": "IF",
            "actionType": "GO_TO",
            "id": logic1Id,
            "createdAt": new Date(),
            "updatedAt": new Date(),
            "targetBlockId": multipleChoice4BlockId,
            "endOnCompletion": false,
            "parentJourneyItemId": multipleChoice3Id,
            "conditions": [
              {
                "conditionOperation": "IS_EQUAL_TO",
                "additionalType": null,
                "id": condition1Id,
                "value": "true",
                "createdAt": new Date(),
                "updatedAt": new Date(),
                "sourceBlockId": multipleChoice3BlockId,
                "parentLogicItemId": logic1Id
              }
            ]
          },
          {
            "conditionType": "IF",
            "actionType": "GO_TO",
            "id": logic2Id,
            "createdAt": new Date(),
            "updatedAt": new Date(),
            "targetBlockId": longText1BlockId,
            "endOnCompletion": false,
            "parentJourneyItemId": multipleChoice3Id,
            "conditions": [
              {
                "conditionOperation": "IS_EQUAL_TO",
                "additionalType": null,
                "id": condition2Id,
                "value": "false",
                "createdAt": new Date(),
                "updatedAt": new Date(),
                "sourceBlockId": multipleChoice3BlockId,
                "parentLogicItemId": logic2Id
              }
            ]
          }
        ]
      },
      {
        "type": "LONG_TEXT",
        "strictness": "COMMON",
        "contentAlignment": "LEFT",
        "mediaPosition": "FULL",
        "mobileMediaPosition": "MOBILE_FULL",
        "id": longText1Id,
        "blockId": longText1BlockId,
        "content": "Ah ok, what's holding you back from automating your processes?",
        "manditory": false,
        "order": 5,
        "selectOptions": [],
        "multiSelect": null,
        "youtubeVideoURL": null,
        "internalName": null,
        "consentTitle": null,
        "minRange": null,
        "maxRange": null,
        "minLabel": null,
        "maxLabel": null,
        "secondaryContent": "Tell us what's getting in the way.",
        "thirdContent": null,
        "forthContent": null,
        "icon": null,
        "additionalSettings": null,
        "targetBlockId": null,
        "endOnCompletion": false,
        "scoreValues": null,
        "responseTableVisible": null,
        "horizontalAlignment": true,
        "opacity": 100,
        "media": {
          "provider": "UNSPLASH",
          "id": longText1MediaId,
          "type": "image",
          "name": "purple and pink light illustration",
          "link": "https://unsplash.com/photos/n2XqPm7Bqhk/download?ixid=M3w1Nzk2MDF8MHwxfHNlYXJjaHwxfHxncmFkaWVudCUyMGJsYWNrfGVufDB8fHx8MTc2NDU1ODE0M3ww",
          "workspaceId": workspaceId,
          "createdAt": new Date(),
          "updatedAt": new Date(),
          "author": "Codioful (Formerly Gradienta)",
          "authorProfile": "https://unsplash.com/@codioful",
          "downloadLink": "https://api.unsplash.com/photos/n2XqPm7Bqhk/download?ixid=M3w1Nzk2MDF8MHwxfHNlYXJjaHwxfHxncmFkaWVudCUyMGJsYWNrfGVufDB8fHx8MTc2NDU1ODE0M3ww"
        },
        "createdAt": new Date(),
        "updatedAt": new Date(),
        "formId": formId,
        "logicItems": [
          {
            "conditionType": "IF",
            "actionType": "GO_TO",
            "id": logic3Id,
            "createdAt": new Date(),
            "updatedAt": new Date(),
            "targetBlockId": endScreenBlockId,
            "endOnCompletion": false,
            "parentJourneyItemId": longText1Id,
            "conditions": []
          }
        ]
      },
      {
        "type": "MULTIPLE_CHOICE",
        "strictness": "COMMON",
        "contentAlignment": "LEFT",
        "mediaPosition": "FULL",
        "mobileMediaPosition": "MOBILE_FULL",
        "id": multipleChoice4Id,
        "blockId": multipleChoice4BlockId,
        "content": "Great! What tools are you using today?",
        "manditory": false,
        "order": 6,
        "selectOptions": [
          {
            "id": mc4Option1Id,
            "order": 0,
            "value": "Zapier"
          },
          {
            "id": mc4Option2Id,
            "order": 1,
            "value": "Notion"
          },
          {
            "id": mc4Option3Id,
            "order": 2,
            "value": "Airtable"
          },
          {
            "id": mc4Option4Id,
            "order": 3,
            "value": "Custom scripts / manual setups"
          }
        ],
        "multiSelect": true,
        "youtubeVideoURL": null,
        "internalName": null,
        "consentTitle": null,
        "minRange": null,
        "maxRange": null,
        "minLabel": null,
        "maxLabel": null,
        "secondaryContent": "Select all that apply.",
        "thirdContent": null,
        "forthContent": null,
        "icon": null,
        "additionalSettings": null,
        "targetBlockId": null,
        "endOnCompletion": false,
        "scoreValues": null,
        "responseTableVisible": null,
        "horizontalAlignment": true,
        "opacity": 100,
        "media": {
          "provider": "UNSPLASH",
          "id": multipleChoice4MediaId,
          "type": "image",
          "name": "purple and pink light illustration",
          "link": "https://unsplash.com/photos/n2XqPm7Bqhk/download?ixid=M3w1Nzk2MDF8MHwxfHNlYXJjaHwxfHxncmFkaWVudCUyMGJsYWNrfGVufDB8fHx8MTc2NDU1ODE0M3ww",
          "workspaceId": workspaceId,
          "createdAt": new Date(),
          "updatedAt": new Date(),
          "author": "Codioful (Formerly Gradienta)",
          "authorProfile": "https://unsplash.com/@codioful",
          "downloadLink": "https://api.unsplash.com/photos/n2XqPm7Bqhk/download?ixid=M3w1Nzk2MDF8MHwxfHNlYXJjaHwxfHxncmFkaWVudCUyMGJsYWNrfGVufDB8fHx8MTc2NDU1ODE0M3ww"
        },
        "createdAt": new Date(),
        "updatedAt": new Date(),
        "formId": formId,
        "logicItems": []
      },
      {
        "type": "OPTION_SCALE",
        "strictness": "COMMON",
        "contentAlignment": "LEFT",
        "mediaPosition": "FULL",
        "mobileMediaPosition": "MOBILE_FULL",
        "id": optionScaleId,
        "blockId": optionScaleBlockId,
        "content": "How happy are you with your current workflow setup?",
        "manditory": true,
        "order": 7,
        "selectOptions": [],
        "multiSelect": null,
        "youtubeVideoURL": null,
        "internalName": null,
        "consentTitle": null,
        "minRange": 1,
        "maxRange": 10,
        "minLabel": "Not at all happy",
        "maxLabel": "Completely happy",
        "secondaryContent": "Give us your honest impression.",
        "thirdContent": null,
        "forthContent": null,
        "icon": null,
        "additionalSettings": null,
        "targetBlockId": null,
        "endOnCompletion": false,
        "scoreValues": null,
        "responseTableVisible": null,
        "horizontalAlignment": true,
        "opacity": 100,
        "media": {
          "provider": "UNSPLASH",
          "id": optionScaleMediaId,
          "type": "image",
          "name": "purple and pink light illustration",
          "link": "https://unsplash.com/photos/n2XqPm7Bqhk/download?ixid=M3w1Nzk2MDF8MHwxfHNlYXJjaHwxfHxncmFkaWVudCUyMGJsYWNrfGVufDB8fHx8MTc2NDU1ODE0M3ww",
          "workspaceId": workspaceId,
          "createdAt": new Date(),
          "updatedAt": new Date(),
          "author": "Codioful (Formerly Gradienta)",
          "authorProfile": "https://unsplash.com/@codioful",
          "downloadLink": "https://api.unsplash.com/photos/n2XqPm7Bqhk/download?ixid=M3w1Nzk2MDF8MHwxfHNlYXJjaHwxfHxncmFkaWVudCUyMGJsYWNrfGVufDB8fHx8MTc2NDU1ODE0M3ww"
        },
        "createdAt": new Date(),
        "updatedAt": new Date(),
        "formId": formId,
        "logicItems": []
      },
      {
        "type": "LONG_TEXT",
        "strictness": "COMMON",
        "contentAlignment": "LEFT",
        "mediaPosition": "FULL",
        "mobileMediaPosition": "MOBILE_FULL",
        "id": longText2Id,
        "blockId": longText2BlockId,
        "content": "What are the biggest pain points in your day-to-day operations?",
        "manditory": true,
        "order": 8,
        "selectOptions": [],
        "multiSelect": null,
        "youtubeVideoURL": null,
        "internalName": null,
        "consentTitle": null,
        "minRange": null,
        "maxRange": null,
        "minLabel": null,
        "maxLabel": null,
        "secondaryContent": "",
        "thirdContent": null,
        "forthContent": null,
        "icon": null,
        "additionalSettings": null,
        "targetBlockId": null,
        "endOnCompletion": false,
        "scoreValues": null,
        "responseTableVisible": null,
        "horizontalAlignment": true,
        "opacity": 100,
        "media": {
          "provider": "UNSPLASH",
          "id": longText2MediaId,
          "type": "image",
          "name": "purple and pink light illustration",
          "link": "https://unsplash.com/photos/n2XqPm7Bqhk/download?ixid=M3w1Nzk2MDF8MHwxfHNlYXJjaHwxfHxncmFkaWVudCUyMGJsYWNrfGVufDB8fHx8MTc2NDU1ODE0M3ww",
          "workspaceId": workspaceId,
          "createdAt": new Date(),
          "updatedAt": new Date(),
          "author": "Codioful (Formerly Gradienta)",
          "authorProfile": "https://unsplash.com/@codioful",
          "downloadLink": "https://api.unsplash.com/photos/n2XqPm7Bqhk/download?ixid=M3w1Nzk2MDF8MHwxfHNlYXJjaHwxfHxncmFkaWVudCUyMGJsYWNrfGVufDB8fHx8MTc2NDU1ODE0M3ww"
        },
        "createdAt": new Date(),
        "updatedAt": new Date(),
        "formId": formId,
        "logicItems": []
      },
      {
        "type": "END_SCREEN",
        "strictness": "COMMON",
        "contentAlignment": "LEFT",
        "mediaPosition": "SPLIT_RIGHT",
        "mobileMediaPosition": "MOBILE_POSTER_TOP",
        "id": endScreenId,
        "blockId": endScreenBlockId,
        "content": "Thanks for your time!",
        "manditory": false,
        "order": 9,
        "selectOptions": [],
        "multiSelect": null,
        "youtubeVideoURL": null,
        "internalName": null,
        "consentTitle": null,
        "minRange": null,
        "maxRange": null,
        "minLabel": null,
        "maxLabel": null,
        "secondaryContent": "Your input helps us build better tools for real teams.",
        "thirdContent": "Visit Our Website",
        "forthContent": "https://www.formsuite.co",
        "icon": null,
        "additionalSettings": null,
        "targetBlockId": null,
        "endOnCompletion": true,
        "scoreValues": null,
        "responseTableVisible": null,
        "horizontalAlignment": true,
        "opacity": 100,
        "media": {
          "provider": "UNSPLASH",
          "id": endScreenMediaId,
          "type": "image",
          "name": "A black background with a white square in the center",
          "link": "https://unsplash.com/photos/iAvPOBKOI_o/download?ixid=M3w1Nzk2MDF8MHwxfHNlYXJjaHw1NHx8Z3JhZGllbnQlMjBibG9ifGVufDB8fHx8MTc2NDU1ODI5MXww",
          "workspaceId": workspaceId,
          "createdAt": new Date(),
          "updatedAt": new Date(),
          "author": "Andrei Castanha",
          "authorProfile": "https://unsplash.com/@andreicastanha",
          "downloadLink": "https://api.unsplash.com/photos/iAvPOBKOI_o/download?ixid=M3w1Nzk2MDF8MHwxfHNlYXJjaHw1NHx8Z3JhZGllbnQlMjBibG9ifGVufDB8fHx8MTc2NDU1ODI5MXww"
        },
        "createdAt": new Date(),
        "updatedAt": new Date(),
        "formId": formId,
        "logicItems": []
      }
    ],
    "snippets": [],
    "metaTitle": "Product Research Survey",
    "gdprSetting": "NEVER",
    "gdprRequired": false,
    "consentMessageTitle": "I consent to the collection of my personal data"
  };
}

export default generateProductResearchSurveyTemplate;